import { Link, useParams, useSearchParams } from "react-router-dom";
import type { BundleRule } from "@bitacorapro/core-engine";
import { EmptyState } from "../components/EmptyState";
import { QueryBoundary } from "../components/QueryBoundary";
import { SkelTable } from "../components/skeleton/SkelTable";
import { useContextVersionDiff } from "../lib/api/queries";

// /contexto/versiones/:id/diff?against=<id> — what changed between two
// context versions of a brand (ADR-0173 fase 2, PR-7): rules added,
// removed, and the ones whose text or items changed (same rule id, new
// version). Read-only; every role that reaches /contexto can read it.

function RuleRows({ rules }: { rules: BundleRule[] }) {
  return (
    <>
      {rules.map((r) => (
        <tr key={r.ruleVersionId}>
          <td className="cell-meta">{r.itemCodes.join(", ")}</td>
          <td>
            <span className="cell-strong">{r.text}</span>
          </td>
          <td className="cell-meta">{r.verification}</td>
        </tr>
      ))}
    </>
  );
}

export function ContextoVersionDiff() {
  const { id } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const against = params.get("against") ?? undefined;
  const diff = useContextVersionDiff(id, against);

  if (!against) {
    return (
      <div className="page">
        <EmptyState
          title="Falta la versión de comparación."
          description="Abre la comparación desde la tabla de versiones."
          action={
            <Link className="btn btn--ghost" to="/contexto">
              Volver
            </Link>
          }
        />
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header__lead">
          <div className="page-header__eyebrow">
            <Link to="/contexto">Contexto del cliente</Link> · versiones
          </div>
          <h1 className="page-header__title">Qué cambió entre versiones</h1>
        </div>
      </div>
      <QueryBoundary
        query={diff}
        label="la comparación"
        errorTitle="No pudimos comparar las versiones."
        skeleton={<SkelTable rows={4} cols={3} headers={["Ítems", "Regla", "Verificación"]} />}
      >
        {(d) => (
          <div className="stack-gap">
            {d.added.length === 0 && d.removed.length === 0 && d.changed.length === 0 && (
              <EmptyState title="Las dos versiones llevan exactamente las mismas reglas." />
            )}
            {d.added.length > 0 && (
              <Section title={`Reglas nuevas (${d.added.length})`}>
                <RuleRows rules={d.added} />
              </Section>
            )}
            {d.removed.length > 0 && (
              <Section title={`Reglas que salieron (${d.removed.length})`}>
                <RuleRows rules={d.removed} />
              </Section>
            )}
            {d.changed.length > 0 && (
              <Section title={`Reglas que cambiaron (${d.changed.length})`}>
                {d.changed.map((c) => (
                  <tr key={c.ruleId}>
                    <td className="cell-meta">{c.after.itemCodes.join(", ")}</td>
                    <td>
                      <div className="cell-meta">
                        <s>{c.before.text}</s>
                      </div>
                      <span className="cell-strong">{c.after.text}</span>
                    </td>
                    <td className="cell-meta">{c.after.verification}</td>
                  </tr>
                ))}
              </Section>
            )}
          </div>
        )}
      </QueryBoundary>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="panel">
      <div className="panel__head">
        <div className="panel__title">{title}</div>
      </div>
      <div className="panel__body--flush">
        <table className="dtable">
          <thead>
            <tr>
              <th>Ítems</th>
              <th>Regla</th>
              <th>Verificación</th>
            </tr>
          </thead>
          <tbody>{children}</tbody>
        </table>
      </div>
    </div>
  );
}
